/**
 * Centralized face quality feedback messages.
 *
 * This module consolidates all user-facing messages related to face positioning,
 * quality checks, and scene validation. Uses i18n for localization.
 *
 * @module face-quality-messages
 */

import type { SceneQualityResult } from '../face-detection';
import { i18n } from '../i18n';

// =============================================================================
// Message Types
// =============================================================================

/**
 * Status message type for UI styling.
 */
export type StatusType = 'info' | 'success' | 'warning' | 'error';

/**
 * Message format variant.
 * - 'short': Concise messages for small UI elements (e.g., "Move closer")
 * - 'detailed': Full sentences for larger status areas
 */
export type MessageFormat = 'short' | 'detailed';

/**
 * Face quality feedback with message and severity.
 */
export interface FaceQualityFeedback {
  message: string;
  type: StatusType;
}

// =============================================================================
// Message Key Types
// =============================================================================

/**
 * All face message keys.
 */
export type FaceMessageKey =
  | 'NO_FACE'
  | 'TOO_FAR'
  | 'TOO_CLOSE'
  | 'OFF_CENTER'
  | 'OUTSIDE_OVAL'
  | 'POOR_LIGHTING'
  | 'GOOD'
  | 'HOLD_STILL'
  | 'PROCESSING'
  | 'LOOKING';

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Maps message keys to i18n translation keys.
 * Short format uses shorter keys like 'centerFace', 'moveCloser'.
 * Detailed format uses full message keys like 'noFace', 'tooFar'.
 */
const SHORT_MESSAGE_KEYS: Record<
  FaceMessageKey,
  keyof typeof i18n extends never ? string : string
> = {
  NO_FACE: 'noFace',
  TOO_FAR: 'moveCloser',
  TOO_CLOSE: 'moveBack',
  OFF_CENTER: 'centerFace',
  OUTSIDE_OVAL: 'outsideOval',
  POOR_LIGHTING: 'poorLighting',
  GOOD: 'perfect',
  HOLD_STILL: 'holdStill',
  PROCESSING: 'processing',
  LOOKING: 'looking',
};

const DETAILED_MESSAGE_KEYS: Record<
  FaceMessageKey,
  keyof typeof i18n extends never ? string : string
> = {
  NO_FACE: 'noFace',
  TOO_FAR: 'tooFar',
  TOO_CLOSE: 'tooClose',
  OFF_CENTER: 'offCenter',
  OUTSIDE_OVAL: 'outsideOval',
  POOR_LIGHTING: 'poorLighting',
  GOOD: 'good',
  HOLD_STILL: 'holdStill',
  PROCESSING: 'processing',
  LOOKING: 'looking',
};

/**
 * Get face positioning message by key with optional format.
 * Messages are localized using the current i18n language.
 *
 * @param key - Message key
 * @param format - 'short' (default) or 'detailed'
 * @returns The appropriate localized message string
 *
 * @example
 * ```typescript
 * getFaceMessage('TOO_FAR'); // "Move closer" (or localized equivalent)
 * getFaceMessage('TOO_FAR', 'detailed'); // "Move closer to the camera"
 * ```
 */
export function getFaceMessage(key: FaceMessageKey, format: MessageFormat = 'short'): string {
  const translations = i18n.getTranslations();
  const i18nKey = format === 'detailed' ? DETAILED_MESSAGE_KEYS[key] : SHORT_MESSAGE_KEYS[key];
  const message = translations.face[i18nKey as keyof typeof translations.face];
  return typeof message === 'string' ? message : key;
}

/**
 * Get feedback message and type based on scene quality result.
 *
 * This function analyzes the SceneQualityResult and returns the appropriate
 * user feedback. It checks conditions in priority order:
 * 1. No face detected (highest priority)
 * 2. Face too small (user needs to move closer)
 * 3. Face too large (user needs to move back)
 * 4. Face off-center
 * 5. Poor lighting
 * 6. All good (success state)
 *
 * @param quality - Scene quality result from face-utils
 * @param format - Message format ('short' or 'detailed')
 * @returns Feedback object with message and status type
 *
 * @example
 * ```typescript
 * const quality = checkSceneQuality(video, landmarks);
 * const feedback = getFeedbackFromQuality(quality);
 * showStatus(feedback.message, feedback.type);
 * // Could show: { message: "Move closer", type: "warning" }
 * ```
 */
export function getFeedbackFromQuality(
  quality: SceneQualityResult,
  format: MessageFormat = 'short'
): FaceQualityFeedback {
  // Priority 1: No face
  if (!quality.faceDetected) {
    return {
      message: getFaceMessage('NO_FACE', format),
      type: 'warning',
    };
  }

  // Priority 2: Face too small
  if (quality.faceTooSmall) {
    return {
      message: getFaceMessage('TOO_FAR', format),
      type: 'warning',
    };
  }

  // Priority 3: Face too large
  if (quality.faceTooLarge) {
    return {
      message: getFaceMessage('TOO_CLOSE', format),
      type: 'warning',
    };
  }

  // Priority 4: Face off-center
  if (!quality.faceInCenter) {
    return {
      message: getFaceMessage('OFF_CENTER', format),
      type: 'warning',
    };
  }

  // Priority 4.5: Face outside oval guide
  if (!quality.faceInsideOval) {
    return {
      message: getFaceMessage('OUTSIDE_OVAL', format),
      type: 'warning',
    };
  }

  // Priority 5: Poor lighting
  if (!quality.lightingOk) {
    return {
      message: getFaceMessage('POOR_LIGHTING', format),
      type: 'warning',
    };
  }

  // All good
  return {
    message: getFaceMessage('GOOD', format),
    type: 'success',
  };
}

/**
 * Check if scene quality indicates the face is properly positioned.
 *
 * @param quality - Scene quality result
 * @returns true if face is detected, centered, and properly sized
 */
export function isFacePositionedCorrectly(quality: SceneQualityResult): boolean {
  return (
    quality.faceDetected &&
    quality.faceInCenter &&
    !quality.faceTooSmall &&
    !quality.faceTooLarge &&
    quality.faceInsideOval
  );
}

// =============================================================================
// Legacy Exports (for backward compatibility)
// =============================================================================

/**
 * Gets short face messages (localized).
 * @deprecated Use getFaceMessage(key, 'short') instead
 */
export function getFaceMessagesShort(): Record<FaceMessageKey, string> {
  const result = {} as Record<FaceMessageKey, string>;
  for (const key of Object.keys(SHORT_MESSAGE_KEYS) as FaceMessageKey[]) {
    result[key] = getFaceMessage(key, 'short');
  }
  return result;
}

/**
 * Gets detailed face messages (localized).
 * @deprecated Use getFaceMessage(key, 'detailed') instead
 */
export function getFaceMessagesDetailed(): Record<FaceMessageKey, string> {
  const result = {} as Record<FaceMessageKey, string>;
  for (const key of Object.keys(DETAILED_MESSAGE_KEYS) as FaceMessageKey[]) {
    result[key] = getFaceMessage(key, 'detailed');
  }
  return result;
}

/**
 * @deprecated Use getFaceMessage(key, 'short') instead
 */
export const FACE_MESSAGES_SHORT = new Proxy({} as Record<FaceMessageKey, string>, {
  get(_target, prop: string) {
    return getFaceMessage(prop as FaceMessageKey, 'short');
  },
});

/**
 * @deprecated Use getFaceMessage(key, 'detailed') instead
 */
export const FACE_MESSAGES_DETAILED = new Proxy({} as Record<FaceMessageKey, string>, {
  get(_target, prop: string) {
    return getFaceMessage(prop as FaceMessageKey, 'detailed');
  },
});
